import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import ServiceTestAgentPicker from './ServiceTestAgentPicker';
import {
    buildPopUpServiceTestAgentPickerGridColumns,
    buildServiceTestAgentPickerGridColumns,
    SERVICE_TEST_DATA_ARRAY_SEARCH_FIELDS,
} from '../../../panels/net-test-now-panels/NetTestNowHelpers';
import { initialSort } from '../../../panels/net-test-now-panels/NetTestNowAgentsFormSection';
import { useColumnStyles } from '../../agents/AgentStyles';
import useAgentsContext from './hooks/AgentsContextProvider';
import { useTestTypeContext } from './hooks/UseTestTypeContext';
import { VOIP_PHONE_TEST_TYPE } from '../../../panels/voip-test-now-panels/VoipTestConstants';
import { AGENT_IDS_FIELD_NAME } from '../ServiceTestsConstants';

/**
 * ServiceTestAgentsSelectSection
 *
 * Renders the 'selected' mode agent picker for service tests using ServiceTestAgentPicker
 * (which mirrors the AgentSelector UX pattern used elsewhere in the application).
 *
 * Maintains local `theAgents` state to sync from the RHF field `{fieldName}.ids` and
 * derives:
 * - `agentsToShow` — agent objects for the currently selected IDs
 * - `agentsForSelection` — available agents not yet selected (shown in picker dialog)
 *
 */
export function ServiceTestAgentsSelectSection() {
    const { availableAgents } = useAgentsContext();
    const { typeName } = useTestTypeContext();

    const classes = useColumnStyles();

    const showVoipRegistration = typeName === VOIP_PHONE_TEST_TYPE;

    const { watch } = useFormContext();
    const idsFromForm = watch(AGENT_IDS_FIELD_NAME);

    const [theAgents, setTheAgents] = useState(idsFromForm || []);

    // Keep theAgents in sync when the form field changes (e.g. adapterType switch restores saved IDs)
    useEffect(() => {
        if (idsFromForm != null) {
            setTheAgents(idsFromForm);
        }
    }, [idsFromForm]);

    // Resolve agent objects for the selected IDs (theAgents may be array or { selection: [] } from picker)
    const agentsToShow = availableAgents.filter((agent) =>
        Array.isArray(theAgents) ? theAgents.includes(agent._id) : theAgents?.selection?.includes(agent._id)
    );

    // Only show agents in the dialog that are not already selected
    const agentsForSelection = availableAgents.filter(
        (agent) => !agentsToShow.some((selected) => selected._id === agent._id)
    );

    return (
        <ServiceTestAgentPicker
            gridColumns={buildServiceTestAgentPickerGridColumns(classes, showVoipRegistration)}
            gridPopupColumns={buildPopUpServiceTestAgentPickerGridColumns(classes, showVoipRegistration)}
            sorters={initialSort}
            dbSelected={idsFromForm || []}
            selectedAgents={agentsToShow}
            addButtonText={t`Add nPoints`}
            removeButtonText={t`Remove nPoints`}
            selectedEmptyGridText={t`Click the Add nPoints button above to select nPoints to run this service test`}
            setTheAgents={setTheAgents}
            dataArray={agentsForSelection}
            dataArraySearchFields={SERVICE_TEST_DATA_ARRAY_SEARCH_FIELDS}
        />
    );
}

export default ServiceTestAgentsSelectSection;
